import type { AxiosResponse } from 'axios'
import { createContext, useContext } from 'react'

import { FilterAdapter } from '../adapters/filterAdapter.types'
import { InsightsColourMap } from '../utils/colourResolver'
import {
  FilterValues,
  InsightsDatasetEnvelope,
  InsightsFilterValuesEnvelope,
  InsightsFiltersEnvelope,
  InsightsMetricEnvelope,
} from '../types/insights.types'

/**
 * The HTTP boundary the host app supplies. Implemented in the app's service
 * layer (where the configured `percentClient` lives) so this library never owns
 * or constructs an HTTP client. The response shape follows the shared
 * `@percent/utility` `useQuery` (Axios-based) the hooks unwrap with — hence the
 * `AxiosResponse` envelope; `axios` is imported as a type only.
 */
export type InsightsService = {
  getMetric: (args: {
    domain: string
    metric: string
    params: Record<string, string | string[] | number>
  }) => Promise<AxiosResponse<InsightsMetricEnvelope>>
  getFilters: (args: {
    domain: string
  }) => Promise<AxiosResponse<InsightsFiltersEnvelope>>
  /** Searchable, keyset-paginated values for one `values` filter (NEX-746). Also
   * serves a `?value=` lookup (repeated param) that resolves `{ value, label }`
   * for an explicit set of already-known values in one call — used to hydrate a
   * restored selection's labels. Optional: unwired, the bar falls back to
   * client-side search over the inline `values`. */
  getFilterValues?: (args: {
    domain: string
    filter: string
    params: Record<string, string | number | string[]>
  }) => Promise<AxiosResponse<InsightsFilterValuesEnvelope>>
  getDataset: (args: {
    domain: string
    dataset: string
    params: Record<string, string | string[] | number>
  }) => Promise<AxiosResponse<InsightsDatasetEnvelope>>
  /**
   * Full-result CSV export — `GET /v1/insights/:domain/datasets/:dataset/csv`
   * (NEX-712). Streams a `text/csv` body, so the host requests it as text and the
   * response `data` is the raw CSV string. Params are serialised filters + a
   * `columns` projection only (the endpoint rejects pagination/sort). Optional:
   * when the host hasn't wired it, the export UI renders but the action is
   * disabled.
   */
  exportDataset?: (args: {
    domain: string
    dataset: string
    params: Record<string, string | string[] | number>
  }) => Promise<AxiosResponse<string>>
}

export type InsightsContextValue = {
  /** The insights domain (e.g. `donations`) set once on `<Insights.Domain>`. */
  domain: string
  service: InsightsService
  /**
   * BCP-47 locale used to format hero values and trends (thousands grouping,
   * currency, comparison date range). Supplied by the host's i18n; defaults to
   * `en-US`, matching the shared `formatMoney` default.
   */
  locale: string
  /** Per-domain series-key → colour overrides; unmapped keys fall back to the palette. */
  colourMap: InsightsColourMap
  /**
   * Canonical series-key order, so every chart that splits by the same series
   * (e.g. donation categories) stacks/legends them identically regardless of
   * which keys each filter happens to surface. Keys not listed sort after.
   */
  seriesOrder: string[]
  filterAdapter: FilterAdapter
  /**
   * Filter values that are always applied to every metric query and locked from
   * the UI: `<Insights.Filters>` hides their controls and they survive Reset.
   * Use to permanently scope a domain panel (e.g. `{ category: ['donations'] }`).
   */
  lockedFilters: FilterValues
}

export const InsightsContext = createContext<InsightsContextValue | undefined>(
  undefined
)

InsightsContext.displayName = 'InsightsContext'

export const useInsightsContext = (): InsightsContextValue => {
  const context = useContext(InsightsContext)

  if (!context) {
    throw new Error(
      'useInsightsContext must be used within an <Insights.Domain>'
    )
  }

  return context
}
